import { ConnectionQuality, VideoQuality } from 'livekit-client';

/**
 * Política de mitigação de rede.
 *
 * Regra de produto: a qualidade LOCAL do PC de um participante NÃO deve
 * degradar a sala para os outros. `setVideoQuality` no subscriber só muda o
 * que ESTE cliente recebe — mas forçar LOW/MEDIUM em todos os remotos faz o
 * anfitrião ver a sala inteira “em batata” e parecer que “o PC dele derrubou
 * a reunião”.
 *
 * Por isso Poor NÃO mexe mais na camada de subscribe. Deixamos o adaptiveStream
 * do LiveKit escolher. Só em Lost pedimos MEDIUM (não 180p).
 *
 * Evidência: tooltip "Ruim" + 0% perda + 180×320 — espiral Poor→LOW.
 */

/**
 * Camada máxima de vídeo RECEBIDO (só neste cliente).
 * `null` = não interferir (adaptiveStream decide).
 */
export function incomingVideoQualityForConnection(
  quality: ConnectionQuality,
): VideoQuality | null {
  switch (quality) {
    case ConnectionQuality.Lost:
      // Link realmente caído — alívio local, sem 180p.
      return VideoQuality.MEDIUM;
    case ConnectionQuality.Poor:
      // Não forçar: Poor falso (TURN/BWE) + setVideoQuality destrói a UI local.
      return null;
    case ConnectionQuality.Good:
    case ConnectionQuality.Excellent:
      return VideoQuality.HIGH;
    default:
      return null;
  }
}

/** Cap duro de publish só em Lost — Poor não prende o encoder. */
export function shouldHardCapPublishBitrate(quality: ConnectionQuality): boolean {
  return quality === ConnectionQuality.Lost;
}

/** Poor/Lost podem aliviar captura local; nunca a dos outros. */
export function shouldTightenCameraCapture(quality: ConnectionQuality): boolean {
  return quality === ConnectionQuality.Poor || quality === ConnectionQuality.Lost;
}

/**
 * Intervalo para "probe" de recuperação quando Poor sustenta.
 * Libera HIGH no subscribe local para o adaptiveStream subir de novo.
 */
export const FALSE_POOR_RECOVERY_INTERVAL_MS = 12_000;

export function shouldAttemptFalsePoorRecovery(
  quality: ConnectionQuality,
  poorSinceMs: number | null,
  now: number,
  lastRecoveryAt: number | null,
): boolean {
  if (quality !== ConnectionQuality.Poor) return false;
  if (poorSinceMs === null) return false;
  if (now - poorSinceMs < FALSE_POOR_RECOVERY_INTERVAL_MS) return false;
  if (
    lastRecoveryAt !== null &&
    now - lastRecoveryAt <= FALSE_POOR_RECOVERY_INTERVAL_MS
  ) {
    return false;
  }
  return true;
}
